"use client"

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"
import { sendMessageToParent, getOpenerOrigin } from "@/lib/app-messaging"

// Local storage key for custom token (development/testing)
const CUSTOM_TOKEN_KEY = "fbo_custom_token"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  tokenSource: "parent" | "custom" | "none"
  saveCustomToken: (token: string) => void
  clearCustomToken: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [tokenSource, setTokenSource] = useState<"parent" | "custom" | "none">("none")
  const calculatorReadySent = useRef(false)

  const setToken = (newToken: string | null) => {
    console.log("AuthContext: Setting token")
    setTokenState(newToken)
    tokenManager.notifyTokenUpdate(newToken)
  }

  // Save a custom token to localStorage (for development/testing)
  const saveCustomToken = (customToken: string) => {
    if (typeof window !== "undefined") {
      localStorage.setItem(CUSTOM_TOKEN_KEY, customToken)
      setToken(customToken)
      setTokenSource("custom")
    }
  }

  // Clear the custom token from localStorage
  const clearCustomToken = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem(CUSTOM_TOKEN_KEY)
      setToken(null)
      setTokenSource("none")
    }
  }

  // On mount, check for saved custom token or request token via postMessage
  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    // Check for a saved custom token (for development/testing)
    const savedToken = localStorage.getItem(CUSTOM_TOKEN_KEY)
    if (savedToken) {
      setToken(savedToken)
      setTokenSource("custom")
      return
    }

    // Request token via postMessage from opener window
    if (!calculatorReadySent.current) {
      calculatorReadySent.current = true
      const urlParams = new URLSearchParams(window.location.search)
      const sourceId = urlParams.get("source") || ""

      console.log("AuthContext: Sending CALCULATOR_READY to opener")
      sendMessageToParent({
        type: "CALCULATOR_READY",
        source: sourceId
      })
    }
  }, [])

  // Listen for AUTH_TOKEN message from opener window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const trustedOrigin = getOpenerOrigin()

        // Fail-closed: If we cannot determine the trusted origin, reject all messages
        if (!trustedOrigin) {
          console.warn("Cannot validate message origin: no trusted origin available")
          return
        }

        if (!window.opener || event.source !== window.opener) {
          console.warn("Rejecting message: source is not opener window")
          return
        }

        // Validate origin against trusted opener origin
        if (event.origin !== trustedOrigin) {
          console.warn(`Rejecting message from untrusted origin: ${event.origin}`)
          return
        }

        const data = event.data

        if (data && data.type === "AUTH_TOKEN" && typeof data.token === "string") {
          console.log("Received AUTH_TOKEN message from opener")
          setToken(data.token)
          setTokenSource("parent")
        }
      } catch (error) {
        console.error("Error processing message:", error)
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [])

  // Update authentication status when token changes
  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated,
        tokenSource,
        saveCustomToken,
        clearCustomToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
